import { useLazyQuery } from '@apollo/client'
import { Button, Typography, useMediaQuery, useTheme } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import Pagination from '@material-ui/lab/Pagination'
import { matchSorter } from 'match-sorter'
import { useEffect, useMemo, useState } from 'react'

import PageHeader from '../../components/PageHeader'
import { TEMPLATES_QUERY } from '../../graphql/contractTemplates'
import { getEntityCode, getRoleFromStorage } from '../../helpers/userData'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import contractPagesRoleManagement from '../../staticRoleManagement/contractRoleManagement'
import { ROLE } from '../../staticRoleManagement/types'
import { TemplateBrief } from '../../types/contractTemplate'
import ContractFilters from './TemplatesFilters'
import TemplatesTable from './TemplatesTable'
import useStyles from './styles/index.styles'

const TemplatesList: React.FC = () => {
  const theme = useTheme()
  const classes = useStyles()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [autoCompleteForProviderCode, setAutoCompleteForProviderCode] =
    useState<any>()
  const [perPage] = useState(5)
  const [page, setPage] = useState(1)
  const [count, setcount] = useState<number>(0)
  const [searchInputValue, setsearchInputValue] = useState<string>('')
  const [filteredContractTemplates, setfilteredContractTemplates] = useState<
    TemplateBrief[]
  >([])
  const [getContractTemplates, { data, loading, refetch }] = useLazyQuery<{
    templates: TemplateBrief[]
  }>(TEMPLATES_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network'
  })

  const validAccommodationsRoles = [
    ROLE.ACCOMMODATION,
    ROLE.ACCOMMODATION_OPERATOR
  ]

  const isRoleProvider = getRoleFromStorage() === ROLE.PROVIDER

  const isRoleAccommodation = validAccommodationsRoles.some(
    (role) => role === getRoleFromStorage()
  )

  useEffect(() => {
    if (autoCompleteForProviderCode && autoCompleteForProviderCode.value) {
      getContractTemplates({
        variables: {
          providerCode:
            autoCompleteForProviderCode && autoCompleteForProviderCode.value
        }
      })
    }
    if (isRoleProvider) {
      getContractTemplates({
        variables: {
          providerCode: getEntityCode()
        }
      })
    }
  }, [
    autoCompleteForProviderCode,
    autoCompleteForProviderCode && autoCompleteForProviderCode.value,
    getRoleFromStorage(),
    getEntityCode()
  ])

  useEffect(() => {
    setPage(1)
    const fullTemplatesList = data?.templates ? data?.templates : []
    const newFilteredContractTemplates = matchSorter(
      fullTemplatesList,
      searchInputValue,
      {
        keys: ['Title'],
        sorter: (rankedItems) =>
          rankedItems.sort((a, b) => {
            return a.item.Title > b.item.Title ? 1 : -1
          })
      }
    )
    setfilteredContractTemplates(newFilteredContractTemplates)
    setcount(newFilteredContractTemplates.length)
  }, [searchInputValue, loading, data])

  const numberOfPages = useMemo(() => Math.ceil(count / perPage), [count])

  const hasAccessToChangeProvider = contractPagesRoleManagement
    .find((item) => item.route === '/contracts/(list|templates)')
    ?.sections?.PROVIDER_SELECTOR?.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )

  return (
    <div>
      <PageHeader title={getTranslate('Templates')} />
      <div className={classes.header}>
        <Typography variant="h3" gutterBottom>
          {getTranslate('Templates List')}
        </Typography>
        {contractPagesRoleManagement
          .find((item) => item.route === '/contracts/(list|templates)')
          ?.sections?.ADD_TEMPLATE_BUTTON.rolesListWithWriteAccess?.includes(
            getRoleFromStorage()
          ) && (
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            color="primary"
            component={LocalizedLink}
            to="/templates/list/create">
            {getTranslate(`Add${isSmallScreen ? '' : ' Template'}`)}
          </Button>
        )}
      </div>
      <ContractFilters
        hasAccessToChangeProvider={hasAccessToChangeProvider}
        setAutoCompleteForProviderCode={setAutoCompleteForProviderCode}
        autoCompleteForProviderCode={autoCompleteForProviderCode}
        inputValue={searchInputValue}
        setinputVal={setsearchInputValue}
        loadingForDisableSearchBox={loading}
      />
      {((autoCompleteForProviderCode && autoCompleteForProviderCode.value) ||
        isRoleProvider ||
        isRoleAccommodation) && (
        <TemplatesTable
          reloadData={refetch}
          data={filteredContractTemplates.slice(
            (page - 1) * perPage,
            page * perPage
          )}
          loading={loading}
          providerCode={
            isRoleProvider
              ? getEntityCode()
              : isRoleAccommodation
              ? autoCompleteForProviderCode
              : (autoCompleteForProviderCode &&
                  autoCompleteForProviderCode.value) ||
                ''
          }
        />
      )}
      {((autoCompleteForProviderCode && autoCompleteForProviderCode.value) ||
        isRoleProvider ||
        isRoleAccommodation) && (
        <div className={classes.paginationContainer}>
          <Pagination
            count={numberOfPages}
            color="primary"
            size={isSmallScreen ? 'small' : 'medium'}
            page={page}
            onChange={(_, value) => setPage(value)}
          />
        </div>
      )}
    </div>
  )
}

export default TemplatesList
